/**
 * Typed fetch client for the admin panel.
 *
 * - Base URL from `import.meta.env.VITE_API_URL` (default `/api/v1`; the dev
 *   Vite proxy maps `/api` -> http://localhost:3000). In prod the build env
 *   points at https://api.yourdose.app/api/v1.
 * - Attaches `Authorization: Bearer <token>` from the auth-token bridge.
 * - Sends/parses JSON; throws a typed `ApiError` on non-2xx carrying the
 *   RFC 7807 Problem Details body (`code`, `detail`, field `errors`).
 * - On 401 it tries a silent token refresh once and replays the request; only
 *   if that fails does it clear auth and redirect to /login.
 *
 * Pages engineers consume the exported `get/post/patch/del` helpers and
 * `buildQuery`. They MUST NOT modify this file.
 */
import type { ProblemDetails } from '../types';
import {
  attemptRefresh,
  getAccessToken,
  notifyUnauthorized,
} from './auth-token';

/** Base API URL incl. version prefix. */
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Error thrown for any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  /** App-level error code (e.g. UNAUTHENTICATED, VALIDATION_FAILED). */
  readonly code: string;
  /** Field-level validation messages, when present. */
  readonly errors?: ProblemDetails['errors'];
  /** Raw Problem Details body (or a synthesized one for network errors). */
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title || `Request failed (${problem.status})`);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.errors = problem.errors;
    this.problem = problem;
  }
}

type Query = Record<
  string,
  string | number | boolean | null | undefined
>;

/** Serialize a flat object into a `?a=1&b=2` query string (skips nullish). */
export function buildQuery(params?: Query): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.append(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

interface RequestOptions {
  /** Query params appended to the URL. */
  query?: Query;
  /** Abort signal (TanStack Query passes one through `queryFn`). */
  signal?: AbortSignal;
  /** Extra headers (rarely needed). */
  headers?: Record<string, string>;
  /**
   * Internal. Skip the 401 -> refresh -> replay lane. Set on the refresh and
   * login calls themselves, which must never re-enter it, and on the single
   * replay, which must not loop.
   */
  noAuthRetry?: boolean;
}

async function request<TResponse>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  const url = `${API_BASE_URL}${path}${buildQuery(options?.query)}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options?.headers,
  };

  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const hasBody = body !== undefined && body !== null;
  if (hasBody) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  } catch (cause) {
    // Network / CORS / abort failure — synthesize a Problem Details body.
    throw new ApiError({
      type: 'about:blank',
      title: 'Network error',
      status: 0,
      detail:
        cause instanceof Error ? cause.message : 'Unable to reach the server.',
      instance: url,
      code: 'NETWORK_ERROR',
    });
  }

  if (response.status === 401) {
    // A 15-minute access token expiring mid-session is the common case here,
    // not a revoked one. Trade the refresh token for a fresh pair and replay
    // once; `attemptRefresh` guarantees a single shared exchange even when a
    // page 401s six queries at the same moment (18_ADMIN_SESSION.md §2).
    if (!options?.noAuthRetry && (await attemptRefresh())) {
      return request<TResponse>(method, path, body, {
        ...options,
        noAuthRetry: true,
      });
    }
    // No refresh available, or it was rejected: clears auth + redirects to
    // /login (handler registered by AuthProvider).
    notifyUnauthorized();
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const text = await response.text();
  const data = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(toProblem(data, response, url));
  }

  return data as TResponse;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function toProblem(data: unknown, response: Response, url: string): ProblemDetails {
  if (data && typeof data === 'object' && 'code' in data) {
    return data as ProblemDetails;
  }
  return {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    detail:
      typeof data === 'string' && data
        ? data
        : `Request failed with status ${response.status}.`,
    instance: url,
    code: 'UNKNOWN',
  };
}

/* ----------------------------------------------------------- public API */

export function get<TResponse>(
  path: string,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('GET', path, undefined, options);
}

export function post<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('POST', path, body, options);
}

export function patch<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('PATCH', path, body, options);
}

export function put<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('PUT', path, body, options);
}

/** DELETE (named `del` since `delete` is a reserved word). */
export function del<TResponse>(
  path: string,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('DELETE', path, undefined, options);
}

export const api = { get, post, patch, put, del, buildQuery, API_BASE_URL };
